// LEGACY: the panel as it was before the Capability release (main before FE #647),
// shown to orgs without the release (utils/capabilityRelease.ts). Not edited:
// delete it once the release is on for every org.
import React, { useState } from 'react';
import { Box, Collapse, Typography, CircularProgress, SxProps, Theme } from '@mui/material';
import DosenTooltip from '../ui/DosenTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { bandRange, formatSectionValue, MedalKey, MEDALS, MedalSection, MEMBER_VIEW_HOW_TO_IMPROVE, PLATINUM_START, SECTION_HELP } from './scorecardData';
import TierTrend from './TierTrend';
import { ADMIN_TIERS, adminIntro, OutcomesTable, type MedalsData } from './MedalScore';
import ElliottFace from '../../pages/new-designs/components/ElliottFace';
import { useAppSelector } from '../../redux/hooks';
import { TierPanelSkeleton, fadeInSx } from '../ui/DashboardSkeletons';
import { NOT_REQUIRED } from '../../utils/requirementsScore';
import { MOBILE_SX } from '../../mobile/mobileApp';

/**
 * Development Tier panel — the learner's half of the dashboard split, mirroring
 * the admin's Gaps & Risks panel and borrowing its brandy framing.
 *
 * A condensed take on the tier modal from #570: header + tier badge, the 6
 * month view, then the five measures as one outlined list. Each measure has its
 * title on top (there isn't the width for it beside the bar), then tier steps,
 * figure and tier pill. The trend icon beside a title opens How to Improve,
 * which lists THIS learner's outstanding items for that measure.
 */

const BRANDY = '#F79420';
const BRANDY_FRAME = 'rgba(247, 148, 32, 0.45)';
const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const TRACK = '#2A2A2A';
// A measure with no tier (nothing required of them, DA-5056 R5): its pill in grey.
const NO_MEDAL = '#565656';

// Fixed columns right of the steps, so the steps end at the same point on
// every row. Sized for "14 msgs" and "PLATINUM".
const VALUE_COL = 50;
const TIER_COL = 66;

// Learners can reach Platinum; an organization's ceiling is Gold.
const LEARNER_TIER_KEYS: MedalKey[] = ['wood', 'bronze', 'silver', 'gold', 'platinum'];
const ORG_TIER_KEYS: MedalKey[] = ['wood', 'bronze', 'silver', 'gold'];

// Shown only on Platinum hovers: the Platinum step, a PLATINUM pill, and the graph's Platinum dot.
export const PLATINUM_NOTE = 'Platinum is awarded to the top 10% of Gold learners.';

// Proactive Learning is the one measure with an absolute Platinum gate rather
// than the top-10% rule, so its band tooltip says so rather than contradicting
// the badge the learner is looking at.
export const PLATINUM_NOTE_PROACTIVE =
  'Unlike in other measures, Platinum for Proactive Learning is awarded if you’ve sent 3 or more messages to Elliott in the last 7 days.';

const platinumNoteFor = (sectionName: string): string =>
  sectionName === 'Proactive Learning' ? PLATINUM_NOTE_PROACTIVE : PLATINUM_NOTE;

/** One thing the learner can do to move a measure. */
export interface TierAction {
  id: string;
  /** Program the action sits in — the eyebrow above the label. Empty to omit. */
  programName: string;
  /** What to do, e.g. "Module 2 · Handling objections". Empty on CTA rows. */
  label: string;
  /** Trailing detail, e.g. "3 questions". */
  note?: string;
  /** Render the row as a button with this label instead of a text line. */
  ctaLabel?: string;
  onClick?: () => void;
}

interface Props {
  medals: MedalsData | null;
  loading: boolean;
  /** Outstanding actions keyed by measure display name ("Knowledge Retention"). */
  actionsByMeasure: Record<string, TierAction[]>;
  actionsLoading: boolean;
  /** Layout overrides for hosts other than the learner dashboard (e.g. the Org Members tier modal). */
  sx?: SxProps<Theme>;
  /**
   * Whose tier this is when an admin views one member (Org Members). Switches
   * the copy to the third person, about that member.
   */
  memberName?: string;
  /** Admin dashboard: org-level copy, How to Improve for admins, Gold ceiling. */
  audience?: 'learner' | 'orgAdmin' | 'groupAdmin';
  /** Inside a host panel that owns the frame (AdminInsightsPanel). */
  embedded?: boolean;
}

// What the tier is and how it's earned. Lives behind the info icon beside the
// title so the trend and the measures get the height. Named to the org, since
// the five measures are the org's yardstick rather than a Dosen-wide one.
const tierExplainer = (orgName: string, memberName?: string): string[] => {
  if (memberName) {
    return [
      `This is ${memberName}'s Learning Tier at ${
        orgName || 'their organization'
      }. Elliott has proactively provided them with instructions on how to improve each measure to advance tiers. Their overall tier is the lowest tier they hold across their five measures.`
    ];
  }
  const org = orgName || 'your organization';
  return [
    'Your Learning Tier measures your development through learning, and follows the Bersin-style L&D maturity model.',
    `Your overall tier at ${org} is the lowest tier you hold across your five measures of development, so it is only ever as strong as your weakest one. You can improve your tier across each measure by following the recommended actions below.`,
    `Your Learning Tier is seen by your L&D and HR teams, management, and senior leadership, who use it to understand how you are developing and to inform decisions about your growth, as a stronger tier reflects an investment in yourself at ${org}.`
  ];
};

/**
 * The tier a measure has reached, read off where it sits on its track. None
 * (null) when nothing it measures is required of them (medal null, DA-5056
 * R5): it shows "-" and no step lit, never Rust.
 */
const reachedTier = (section: MedalSection, showPlatinum: boolean): MedalKey | null => {
  if (section.medal === null) return null;
  const [b1, b2, b3] = section.boundaries;
  if (showPlatinum && section.pos >= PLATINUM_START) return 'platinum';
  if (section.pos >= b3) return 'gold';
  if (section.pos >= b2) return 'silver';
  if (section.pos >= b1) return 'bronze';
  return 'wood';
};

/** Outlined tier chip — the headline badge (large) and each measure's pill; "-" in grey with no tier. */
const TierPill: React.FC<{ medalKey: MedalKey | null; large?: boolean }> = ({ medalKey, large = false }) => {
  const m = medalKey ? MEDALS[medalKey] : { color: NO_MEDAL, label: NOT_REQUIRED };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flexShrink: 0,
        minWidth: large ? 0 : 58,
        px: large ? '16px' : '6px',
        py: large ? '8px' : '4px',
        borderRadius: '4px',
        // `${hex}1F` / `${hex}55` = the tier colour at ~12% / ~33% alpha.
        bgcolor: `${m.color}1F`,
        border: `1px solid ${m.color}`,
        boxShadow: large ? `0 0 10px ${m.color}55` : 'none',
        color: m.color,
        fontFamily: 'Inter, sans-serif',
        fontSize: large ? 18 : 9,
        fontWeight: 700,
        letterSpacing: large ? '1px' : '0.4px',
        lineHeight: 1,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      }}
    >
      {large && <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: m.color }} />}
      {m.label}
    </Box>
  );
};

/**
 * The measure's tier as steps: one segment per tier, lit up to the tier it
 * holds. Hovering a segment names the tier and the bracket it covers
 * ("RUST 0–24%", or messages on Proactive Learning).
 */
const TierSteps: React.FC<{ section: MedalSection; reached: MedalKey | null; showPlatinum: boolean }> = ({
  section,
  reached,
  showPlatinum
}) => {
  const tierKeys = showPlatinum ? LEARNER_TIER_KEYS : ORG_TIER_KEYS;
  // No tier (nothing required of them): no step lit.
  const reachedIndex = reached ? tierKeys.indexOf(reached) : -1;
  const [b1, b2, b3] = section.boundaries;
  // Without Platinum, Gold runs to the end of the track.
  const edges = showPlatinum ? [0, b1, b2, b3, PLATINUM_START, 100] : [0, b1, b2, b3, 100];
  const TIER_KEYS = tierKeys;
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: '3px' }}>
      {tierKeys.map((key, i) => (
        <DosenTooltip
          key={key}
          // Opens DOWNWARD, away from the Improve Your Tier button in the row
          // above, and never takes pointer events — so even when MUI flips it
          // back to the top near a viewport edge, the click still lands on the
          // button underneath rather than on the tooltip.
          placement="bottom"
          componentsProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}
          title={
            <>
              <Box component="span" sx={{ color: MEDALS[key].color, fontWeight: 700 }}>
                {MEDALS[key].label.toUpperCase()}
              </Box>{' '}
              {bandRange(section, edges[i], edges[i + 1], i === TIER_KEYS.length - 1, i)}
              {key === 'platinum' && <Box sx={{ mt: '4px' }}>{platinumNoteFor(section.name)}</Box>}
            </>
          }
        >
          {/* Taller transparent hit area around the 6px step. */}
          <Box sx={{ flex: 1, py: '5px', cursor: 'pointer' }}>
            <Box sx={{ height: 6, borderRadius: '3px', bgcolor: i <= reachedIndex ? MEDALS[key].color : TRACK }} />
          </Box>
        </DosenTooltip>
      ))}
    </Box>
  );
};

/** Brandy CTA — the action itself, for measures whose fix is one click. */
const ActionButton: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    disabled={!onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      mt: '6px',
      py: '5px',
      px: '10px',
      border: 'none',
      borderRadius: '4px',
      background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
      color: '#FDFDFD',
      fontFamily: 'Inter, sans-serif',
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.3,
      cursor: onClick ? 'pointer' : 'not-allowed',
      transition: 'opacity 0.15s',
      '&:hover': { opacity: onClick ? 0.92 : 1 },
      // House disabled state: dark with dim text, not a faded orange.
      '&:disabled': { background: 'rgba(0, 0, 0, 0.2)', color: '#565656', boxShadow: 'inset 0 0 0 1px #2A2A2A' }
    }}
  >
    {label}
  </Box>
);

/** One outstanding item inside a How to Improve dropdown. */
const ActionRow: React.FC<{ action: TierAction }> = ({ action }) => (
  <Box
    onClick={action.ctaLabel ? undefined : action.onClick}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      py: '6px',
      cursor: !action.ctaLabel && action.onClick ? 'pointer' : 'default',
      '&:not(:last-of-type)': { borderBottom: `1px solid ${BORDER_DIM}` },
      '&:hover .tier-action-label': { color: action.onClick ? BRANDY : TEXT_PRIMARY }
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {action.programName && (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: TEXT_TERTIARY,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' }
          }}
        >
          {action.programName}
        </Typography>
      )}
      {action.label && (
        <Typography
          className="tier-action-label"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 500,
            color: TEXT_PRIMARY,
            transition: 'color 0.15s'
          }}
        >
          {action.label}
        </Typography>
      )}
      {action.ctaLabel && <ActionButton label={action.ctaLabel} onClick={action.onClick} />}
    </Box>
    {action.note && (
      <Typography
        sx={{
          flexShrink: 0,
          fontFamily: 'Inter, sans-serif',
          fontSize: 10.5,
          fontWeight: 600,
          color: BRANDY,
          whiteSpace: 'nowrap',
          mt: '12px'
        }}
      >
        {action.note}
      </Typography>
    )}
  </Box>
);

/**
 * One measure row: title + How to Improve icon on top, then the steps, figure
 * and tier pill; How to Improve expands beneath.
 */
const MeasureRow: React.FC<{
  section: MedalSection;
  actions: TierAction[];
  actionsLoading: boolean;
  memberName?: string;
  first: boolean;
  isAdmin: boolean;
}> = ({ section, actions, actionsLoading, memberName, first, isAdmin }) => {
  const [open, setOpen] = useState(false);
  // An admin reading one member's tier gets the learner definition (it's the
  // same measure) with the third-person instruction.
  const help = SECTION_HELP[section.name]?.[isAdmin && !memberName ? 'admin' : 'learner'];
  const definition = help?.definition;
  const howToImprove = memberName ? MEMBER_VIEW_HOW_TO_IMPROVE[section.name]?.(memberName) : help?.howToImprove;
  const hasActions = actions.length > 0;
  // The pill follows the step the measure actually reached, so a learner in the
  // Platinum stretch reads PLATINUM rather than the payload's GOLD. Orgs stop at Gold.
  const reached = reachedTier(section, !isAdmin);

  return (
    <Box
      sx={{
        // Rows share the list's height equally, so there's no empty space under
        // the last one; content sits centred in each row.
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: '10px',
        py: '8px',
        // Faint brandy divide between measures.
        borderTop: first ? 'none' : '1px solid rgba(247, 148, 32, 0.18)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Typography
          sx={{
            minWidth: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12.5,
            fontWeight: 600,
            color: TEXT_PRIMARY,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible' }
          }}
        >
          {section.name}
        </Typography>
        {/* How to Improve — a rising trend line, since that's what acting on it
            does. The button labels itself, so it carries no hover copy. */}
        <Box
          component="button"
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={`How to improve ${section.name}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            // Always boxed, so How to Improve reads as a control: brandy when
            // there's something to do (or it's open), neutral otherwise.
            p: '2px 6px',
            border: `1px solid ${open || hasActions ? BRANDY : '#565656'}`,
            borderRadius: '4px',
            bgcolor: open
              ? 'rgba(247, 148, 32, 0.16)'
              : hasActions
              ? 'rgba(247, 148, 32, 0.06)'
              : 'rgba(255, 255, 255, 0.03)',
            color: hasActions || open ? BRANDY : TEXT_TERTIARY,
            cursor: 'pointer',
            transition: 'filter 0.15s, background-color 0.15s',
            '&:hover': { filter: 'brightness(1.2)' }
          }}
        >
          {/* Labelled so the control names itself; the count always shows, so
                "0" reads as nothing outstanding rather than a missing number. */}
          <Box
            component="span"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap'
            }}
          >
            Improve Your Tier
          </Box>
          <TrendingUpIcon sx={{ fontSize: 16, display: 'block' }} />
          <Box component="span" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>
            {actions.length}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '2px' }}>
        <TierSteps section={section} reached={reached} showPlatinum={!isAdmin} />
        {(() => {
          // Proactive Learning's "None in 30 days" becomes "+30d"; the sentence
          // moves into a tooltip. Every other measure reads as before, and an
          // empty title means DosenTooltip renders nothing.
          const { text, tooltip } = formatSectionValue(section.name, section.value);
          return (
            <DosenTooltip
              title={tooltip}
              placement="bottom"
              componentsProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}
            >
              <Typography
                sx={{
                  width: VALUE_COL,
                  flexShrink: 0,
                  textAlign: 'right',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  whiteSpace: 'nowrap',
                  ...(tooltip ? { cursor: 'help' } : null)
                }}
              >
                {text}
              </Typography>
            </DosenTooltip>
          );
        })()}
        <Box sx={{ width: TIER_COL, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
          {/* Only a PLATINUM pill explains itself; an empty title shows no tooltip. */}
          <DosenTooltip
            title={reached === 'platinum' ? PLATINUM_NOTE : ''}
            placement="bottom"
            componentsProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}
          >
            <Box
              component="span"
              sx={{ display: 'inline-flex', cursor: reached === 'platinum' ? 'pointer' : 'default' }}
            >
              <TierPill medalKey={reached} />
            </Box>
          </DosenTooltip>
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: '8px', ml: '2px', pl: '10px', borderLeft: `2px solid ${BRANDY}` }}>
          {definition && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11.5,
                lineHeight: 1.45,
                color: TEXT_SECONDARY,
                mb: '6px'
              }}
            >
              <Box component="span" sx={{ color: BRANDY, fontWeight: 700 }}>
                Definition:
              </Box>{' '}
              {definition}
            </Typography>
          )}
          {howToImprove && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11.5,
                lineHeight: 1.45,
                color: TEXT_SECONDARY,
                mb: hasActions || actionsLoading ? '8px' : 0
              }}
            >
              <Box component="span" sx={{ color: BRANDY, fontWeight: 700 }}>
                How To Improve:
              </Box>{' '}
              {howToImprove}
            </Typography>
          )}
          {/* Admins get the guidance only — per-lesson action lists are a learner thing. */}
          {isAdmin ? null : actionsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}>
              <CircularProgress size={12} sx={{ color: TEXT_TERTIARY }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: TEXT_TERTIARY }}>
                {memberName ? 'Finding their outstanding items…' : 'Finding your outstanding items…'}
              </Typography>
            </Box>
          ) : hasActions ? (
            actions.map((action) => <ActionRow key={action.id} action={action} />)
          ) : (
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: TEXT_TERTIARY }}>
              Nothing outstanding on this measure right now.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const DevelopmentTierPanel: React.FC<Props> = ({
  medals,
  loading,
  actionsByMeasure,
  actionsLoading,
  sx,
  memberName,
  audience = 'learner',
  embedded = false
}) => {
  const isAdmin = audience !== 'learner';
  const orgName = useAppSelector((state) => state?.orgDetails?.data?.name) || '';
  const explainer = isAdmin && !memberName ? adminIntro(orgName) : tierExplainer(orgName, memberName);
  return (
    // Outlined in brandy and set apart from the pathway columns, which are flat
    // #1C1C1C panels divided by hairlines. Embedded, the host owns the frame.
    <Box
      sx={[
        embedded
          ? { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', p: '12px', gap: '10px' }
          : {
              flex: '0 0 30%',
              minWidth: 300,
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: '12px',
              mr: '8px',
              gap: '10px',
              bgcolor: SURFACE,
              border: `1px solid ${BRANDY_FRAME}`,
              borderRadius: '8px',
              boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)'
            },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {/* Header — Elliott + title + explainer icon centred. Embedded, the host
          panel's toggle names the view and carries the explainer instead. */}
      <Box
        sx={{
          flexShrink: 0,
          display: embedded ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <ElliottFace size={24} glow={false} />
          {/* Same type as the pillar titles opposite: Inter 18/600, uppercase, white. */}
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: TEXT_PRIMARY,
              textAlign: 'center'
            }}
          >
            {memberName ? 'Learning Tier' : 'Your Learning Tier'}
          </Typography>
          <DosenTooltip
            tapOnPhone
            placement="bottom"
            componentsProps={{ tooltip: { sx: { maxWidth: 400 } } }}
            title={
              <Box>
                {explainer.map((paragraph, i) => (
                  <Typography
                    key={paragraph.slice(0, 24)}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 11.5,
                      lineHeight: 1.5,
                      color: TEXT_SECONDARY,
                      mb: i === explainer.length - 1 ? 0 : '8px'
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: BRANDY, cursor: 'pointer', flexShrink: 0 }} />
          </DosenTooltip>
        </Box>
      </Box>

      {loading ? (
        <TierPanelSkeleton />
      ) : !medals || medals.sections.length === 0 ? (
        <Typography
          sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_TERTIARY, textAlign: 'center' }}
        >
          {memberName
            ? `${memberName}'s development tier will appear once they have activity across their programs.`
            : isAdmin
            ? 'Your Learning Tier will appear once learners in these teams have activity across their programs.'
            : 'Your development tier will appear once you have activity across your programs.'}
        </Typography>
      ) : (
        // Fades in where the placeholder was; same column layout and gap as the panel.
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
          {/* 6 month view. Always shown: this week reads the live tier, and past
              weeks without a saved snapshot read Rust. */}
          <TierTrend
            compact
            showPlatinum={!isAdmin}
            history={medals.history ?? null}
            current={medals.overall}
            // Admins: the predicted outcomes by tier live on the graph's tier badge.
            badgeTooltip={
              isAdmin ? (
                // Fixed width + nowrap cells so no outcome figure wraps onto a second line.
                <Box sx={{ width: 640, '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>
                  <OutcomesTable overall={medals.overall} tiers={ADMIN_TIERS} />
                </Box>
              ) : undefined
            }
          />

          {/* The five measures as one outlined list. An expanded How to Improve
              scrolls in place; scrollbarGutter stops the rows jumping sideways. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarGutter: 'stable',
              scrollbarWidth: 'thin',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${BORDER_DIM}`,
              borderRadius: '6px'
            }}
          >
            {medals.sections.map((section, i) => (
              <MeasureRow
                key={section.name}
                section={section}
                actions={actionsByMeasure[section.name] || []}
                actionsLoading={actionsLoading}
                memberName={memberName}
                first={i === 0}
                isAdmin={isAdmin}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DevelopmentTierPanel;
